/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import { TargetUserGroups } from '@Policy/Components/TargetUserGroups';
import { useBoxExceptionUserGroups } from '@Policy/hooks';
import EditableList from '@Components/EditableList';
import { storeSelector, usePolicyContext } from '@Policy/store';
import isEqual from 'lodash/isEqual';
import { TargetCapacity } from '@Policy/utils/types';

export const ExceptionUserGroupsBox = () => {
  const props = useBoxExceptionUserGroups();
  useWhyDidYouUpdate('TeamsTargetUserGroups', {
    ...props,
  });
  return <TargetUserGroups {...props} />;
};
const ExceptionModal = (props: {
  isVisible: boolean;
  editIndex: number;
  onClose: () => void;
  // set the tempVars.slfExceptionUsers to slfExceptionUsers
  // set the tempVars.slfExceptionPaths to slfExceptionPaths
  // clear tempVars slfExceptionUsers and slfExceptionPaths
  saveConfig: (index: number) => void;
}) => {
  const { isVisible, onClose, saveConfig, editIndex } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const onSave = React.useCallback(() => {
    saveConfig(editIndex);
    onClose();
  }, [editIndex]);

  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);

  const exceptionPaths = contextSelector(
    (s) => s.tempVars.slfExceptionPaths,
    isEqual
  );
  const slfExceptionUsers = contextSelector(
    (s) => s.tempVars.targetUserGroups,
    isEqual
  );
  const addPathToExceptionPaths = contextSelector(
    (s) => s.addPathToExceptionPaths,
    isEqual
  );
  const removePathFromExceptionPaths = contextSelector(
    (s) => s.removePathFromExceptionPaths,
    isEqual
  );
  const exceptionPathsDisplay = React.useMemo(() => {
    return exceptionPaths.map((path: string) => ({
      value: path,
      name: `/All Files${path}`,
    }));
  }, [exceptionPaths]);
  const saveDisabled = React.useMemo(() => {
    if (exceptionPaths.length === 0) {
      return true;
    }
    if (
      slfExceptionUsers.capacity !== TargetCapacity.all &&
      slfExceptionUsers.targets.length +
        slfExceptionUsers.targetGroups.length ===
        0
    ) {
      return true;
    }
    if (slfExceptionUsers.empty) {
      return true;
    }
  }, [slfExceptionUsers, exceptionPaths]);
  useWhyDidYouUpdate('ShareLink ExceptionModal', {
    ...props,
  });

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={isVisible}
      onClose={onClose}
      size="auto"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent backgroundColor={colorStyle.background.secondary}>
        <ModalHeader></ModalHeader>

        <ModalBody ml="24px" mt="16px" mb="16px">
          <Flex direction="column">
            <Flex mb="16px">
              <Text>Exception users</Text>
            </Flex>
            <ExceptionUserGroupsBox />
            <Text mt="16px">{t('policy.shareLink.exceptionPath.text')}</Text>
            <EditableList
              placeholder={t('policy.shareLink.exceptionPath.placeholder')}
              onAdd={addPathToExceptionPaths}
              onDelete={removePathFromExceptionPaths}
              list={exceptionPathsDisplay}
              limit={10}
              size={4}
              disabled={false}
              width="1088px"
              description=""
            />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="primary"
            minWidth="20x"
            onClick={onSave}
            disabled={saveDisabled}
          >
            {t('common.button.save')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(ExceptionModal);
